import React, { useState, useEffect } from "react";
import { ModalX } from "../../components";
import { Alert, Button, Input, Option, Select, Spinner, Tab, TabPanel, Tabs, TabsBody, TabsHeader, Textarea } from "@material-tailwind/react";
import { CalendarIcon, ListIcon, UserIcon } from "lucide-react";
import { AddNewMeetingRoomBooking, GetMeetingRoomBookings, GetMeetingRooms } from "../../services/backend";
import moment from "moment";
import { minutesToText, Notify, NotifyError } from "../../utils/functions";
import { Calendar, momentLocalizer } from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './style.css'
import { useGetUsers } from "../../custom-hooks/use-users";
import Autocomplete from '../../components/autocomplete';
import { Link } from "react-router-dom";



const localizer = momentLocalizer(moment)

const INITIAL_FORM_DATA_FOR_BOOKING = {
    user: null,
    meetingRoom: '',
    title: '',
    description: '',
    startDate: '',
    slot: '',
}
const ReceptionistMeetingRoom = () => {

    // State variables
    const [meetingRooms, setMeetingRooms] = useState([]);
    // const [googleMapsLinkStatus, setGoogleMapsLinkStatus] = useState('');
    // const [isLoadingCoordinates, setIsLoadingCoordinates] = useState(false);
    const [refreshRequired, setRefreshRequired] = useState(0);
    const [loading, setLoading] = useState(true);

    const [bookingFormData, setBookingFormData] = useState(INITIAL_FORM_DATA_FOR_BOOKING);
    const [modalOpenForBooking, setModalOpenForBooking] = useState(false);
    const [meetingRoomBookings, setMeetingRoomBookings] = useState([]);
    const [events, setEvents] = useState([]);
    const [view, setView] = useState('month');
    const [loadingMutation, setLoadingMutation] = useState(false);

    const { data: users, isLoading: isLoadingUsers } = useGetUsers({
        _limit: 1000,
        // role: [STATIC_USER_ROLES.USER]
    })

    useEffect(() => {
        const fetchMeetingRooms = async () => {
            setLoading(true);
            const response = await GetMeetingRooms();
            setMeetingRooms(response.data.meetingRooms);
            setLoading(false);
        }
        const fetchMeetingRoomBookings = async () => {
            const response = await GetMeetingRoomBookings();
            setMeetingRoomBookings(response.data.meetingRoomBookings);
            const events = response.data.meetingRoomBookings.map((booking) => ({
                title: booking.title,
                start: new Date(booking.startDate),
                end: new Date(booking.endDate),
                resourceId: booking.meetingRoom._id,
                color: booking.meetingRoom.idColor,
            }));
            setEvents(events);
        }
        fetchMeetingRoomBookings();
        fetchMeetingRooms();
    }, [refreshRequired]);

    const handleOnTimeSlotSelect = (slotInfo) => {
        if (view === 'day' || view === 'week') {
            setBookingFormData({
                ...bookingFormData,
                startDate: slotInfo.start,
                meetingRoom: slotInfo.resourceId,
            });
            setModalOpenForBooking(true);
        }
        else if (view === 'month') {
            setView('day');
        }
    };

    const handleBookingFormSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoadingMutation(true);
            // Add new building logic
            const newMeetingRoomBooking = {
                user: bookingFormData.user?.value || null,
                meetingRoom: bookingFormData.meetingRoom,
                title: bookingFormData.title,
                description: bookingFormData.description,
                startDate: bookingFormData.startDate,
                slot: bookingFormData.slot,
            };
            await AddNewMeetingRoomBooking(newMeetingRoomBooking);
            setRefreshRequired(refreshRequired + 1);
            Notify('Meeting Room Booked Successfully', 'success');
            setModalOpenForBooking(false);
            setBookingFormData(INITIAL_FORM_DATA_FOR_BOOKING);
        }
        catch (error) {
            console.error(error);
            NotifyError(error.response.data.message, error);
        } finally {
            setLoadingMutation(false);
        }
    }

    return (
        <div className={`p-4`}>
            <div className="flex justify-between items-center mb-4">
                <h1 className="md:text-2xl font-thin">Meeting Room Calendar</h1>

            </div>
            <div className="p-4 border rounded-3xl">
                <div>
                    <Tabs value="calendar">
                        <TabsHeader className='bg-gray-200 border border-gray-200 shadow-none rounded-2xl'>
                            <Tab value="calendar">
                                <div className='flex items-center gap-2 font-thin'>
                                    <CalendarIcon className='w-4 h-4' /> Calendar
                                </div>
                            </Tab>
                            <Tab value="list">
                                <div className='flex items-center gap-2 font-thin'>
                                    <ListIcon className='w-4 h-4' /> List
                                </div>
                            </Tab>
                        </TabsHeader>
                        <TabsBody>
                            <TabPanel value="calendar">
                                <Calendar
                                    localizer={localizer}
                                    events={events}
                                    eventPropGetter={(event) => {
                                        return {
                                            className: 'text-xs font-bold',
                                            style: {
                                                backgroundColor: event.color || 'rgb(228, 148, 0)',
                                                borderRadius: '0',
                                                border: '0px solid rgb(228, 148, 0)',
                                            }
                                        }
                                    }}
                                    selectable={view === 'day' || view === 'week' || view === 'month'}
                                    onSelectSlot={handleOnTimeSlotSelect}
                                    onView={setView}
                                    view={view}
                                    startAccessor="start"
                                    endAccessor="end"
                                    style={{ height: 700 }}
                                    resources={meetingRooms.map((meetingRoom) => ({
                                        id: meetingRoom._id,
                                        title: meetingRoom.code
                                    }))}
                                />
                            </TabPanel>
                            <TabPanel value="list">
                                {
                                    loading ?
                                        <div className="flex justify-center">
                                            <Spinner />
                                        </div> : (meetingRoomBookings.length === 0 ?
                                            <div className="flex justify-center mt-4">
                                                <Alert color="brown" variant="outlined">
                                                    Click on the Date to book a Meeting Room.
                                                </Alert>
                                            </div> :
                                            <div className="overflow-x-auto max-h-[700px] overflow-y-auto">
                                                <table className="w-full min-w-max table-auto text-left">
                                                    <thead className="text-sm">
                                                        <tr className="py-3">
                                                            <th colSpan={4} className="py-3 text-xs font-light text-orange-900">List of All Bookings</th>
                                                        </tr>
                                                        <tr className="py-3 uppercase">
                                                            <th className="py-3">Meeting Room</th>
                                                            <th className="py-3">Booked For</th>
                                                            <th className="py-3">Booked By</th>
                                                            <th className="py-3">Booking Time</th>
                                                            <th className="py-3">Booking Duration</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {meetingRoomBookings.map((meetingRoomBooking, index) => (
                                                            <tr key={index} draggable className="border-t hover:bg-gray-50 button rounded-full">
                                                                <td className="font-thin py-3 text-sm">
                                                                    <div className="text-sm font-bold">
                                                                        <div className="flex items-center gap-1">
                                                                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: meetingRoomBooking.meetingRoom.idColor }}></div>
                                                                            <div>{meetingRoomBooking.meetingRoom.code}</div>
                                                                            <div className="text-xs text-gray-500">
                                                                                {meetingRoomBooking.meetingRoom.floor.floorCode}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="text-xs text-gray-500">
                                                                        {meetingRoomBooking.meetingRoom.floor.building.name} - {meetingRoomBooking.meetingRoom.floor.building.branch.name}
                                                                    </div>
                                                                </td>
                                                                <td className="py-3 text-sm">
                                                                    <Link to={`/customer/${meetingRoomBooking.user._id}`}
                                                                        className='text-sm font-thin text-blue-600 hover:text-orange-600 duration-300 underline flex items-center gap-1'>
                                                                        <UserIcon size={12} /> {meetingRoomBooking.user.name}
                                                                    </Link>
                                                                </td>
                                                                <td className="font-thin py-3 text-sm">
                                                                    {meetingRoomBooking.createdBy?.name === meetingRoomBooking.user.name ?
                                                                        <div className='text-xs font-thin text-gray-500'>
                                                                            Self Booked
                                                                        </div> :
                                                                        <div className='text-sm font-bold'>
                                                                            {meetingRoomBooking.createdBy?.name}
                                                                        </div>}
                                                                </td>
                                                                <td className="font-thin py-3 text-sm">
                                                                    <div className='text-sm font-bold'>
                                                                        {moment(meetingRoomBooking.startDate).format('LT')} - {moment(meetingRoomBooking.endDate).format('LT')}
                                                                    </div>
                                                                    <div className='text-xs text-gray-500'>
                                                                        {moment(meetingRoomBooking.createdDate).format('lll')}
                                                                    </div>
                                                                </td>
                                                                <td className="font-thin py-3 text-sm">
                                                                    {minutesToText(meetingRoomBooking.slot)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>

                                                </table>
                                            </div>)
                                }
                            </TabPanel>
                        </TabsBody>
                    </Tabs>
                </div>


                <ModalX
                    isOpen={modalOpenForBooking}
                    onClose={() => setModalOpenForBooking(false)}
                    title={
                        <div className='flex items-center justify-between gap-2 w-full'>
                            <div className='text-lg font-thin'>Book Meeting Room</div>
                            <div className='text-md font-thin'>
                                {moment(bookingFormData.startDate).format('h:mm A')} - {
                                    bookingFormData.slot ?
                                        moment(bookingFormData.startDate).add(bookingFormData.slot, 'minutes').format('h:mm A') :
                                        <span>Slot <span className="text-red-500">*</span></span>

                                }
                                <div className='text-xs text-orange-900'>
                                    {moment(bookingFormData.startDate).format('LL')}
                                </div>
                            </div>
                        </div>
                    }
                    maxWidth="xl">
                    <form onSubmit={handleBookingFormSubmit}>
                        <div className="space-y-2">
                            <div>
                                <Autocomplete
                                    placeholder={<>Select Customer <span className="text-red-500 ml-1">*</span></>}
                                    isLoading={isLoadingUsers}
                                    options={
                                        users?.users?.map((user) => ({
                                            value: user._id,
                                            label: <div className='flex items-center justify-between gap-2'>
                                                <div className='flex items-center gap-2'>
                                                    <div className='w-4 h-4 rounded-full'>
                                                        <UserIcon size={12} />
                                                    </div>
                                                    {user.name}
                                                </div>
                                                <div className='text-xs text-gray-500'>
                                                    {user.email}
                                                </div>
                                            </div>,
                                            searchLabel: user.name + ' (' + user.email + ')'
                                        })) || []}
                                    isClearable={true}
                                    required={true}
                                    getOptionLabel={(option) => option.label}
                                    getOptionValue={(option) => option.searchLabel}
                                    onChange={(newValue) => setBookingFormData({ ...bookingFormData, user: newValue })}
                                    value={bookingFormData.user}
                                />
                            </div>
                            <div>
                                <Select
                                    label={<>Select Meeting Room <span className="text-red-500 ml-1">*</span></>}
                                    value={bookingFormData.meetingRoom}
                                    onChange={newValue => setBookingFormData({ ...bookingFormData, meetingRoom: newValue })}
                                >
                                    {
                                        meetingRooms.map((meetingRoom, index) => (
                                            <Option key={index} value={meetingRoom._id}>
                                                {meetingRoom.number} ({meetingRoom.code}) in {meetingRoom.floor.building.name}
                                            </Option>
                                        ))
                                    }
                                </Select>
                            </div>
                            <div>
                                <Input
                                    label="Title"
                                    name="title"
                                    required
                                    value={bookingFormData.title}
                                    onChange={(e) => setBookingFormData({ ...bookingFormData, title: e.target.value })}
                                />
                            </div>
                            <div>
                                <Textarea
                                    label="Description"
                                    name="description"
                                    value={bookingFormData.description}
                                    onChange={(e) => setBookingFormData({ ...bookingFormData, description: e.target.value })}
                                />
                            </div>
                            <div>
                                <Select
                                    label={<>Slot <span className="text-red-500 ml-1">*</span></>}
                                    value={bookingFormData.slot}
                                    onChange={newValue => setBookingFormData({ ...bookingFormData, slot: newValue })}
                                >
                                    <Option value={'30'}>30 Minutes</Option>
                                    <Option value={'60'}>1 Hour</Option>
                                    <Option value={'90'}>1 Hour, 30 Minutes</Option>
                                    <Option value={'120'}>2 Hours</Option>
                                </Select>
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end space-x-2">
                            <Button
                                onClick={() => setModalOpenForBooking(false)}
                                className="rounded-full "
                                variant="text"
                            >
                                Cancel
                            </Button>
                            <Button type="submit" className="rounded-full" disabled={loadingMutation} loading={loadingMutation}>
                                Book
                            </Button>
                        </div>
                    </form>
                </ModalX>
            </div>
            <div className={`p-4 border rounded-3xl mt-2`}>
                <div className="flex justify-between items-center mb-4">
                    <h1 className="md:text-2xl font-thin">Meeting Rooms</h1>
                </div>
                {
                    loading ?
                        <div className="flex justify-center">
                            <Spinner />
                        </div> : (meetingRooms.length === 0 ?
                            <div className="flex justify-center mt-4">
                                <Alert color="brown" variant="outlined">
                                    Click on the button to add a Meeting Room.
                                </Alert>
                            </div> :
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-max table-auto text-left">
                                    <thead className="text-sm">
                                        <tr className="py-3">
                                            <th colSpan={4} className="py-3 text-xs font-light text-orange-900">List of All Meeting Rooms</th>
                                            {/* <th colSpan={2} className="py-3">
                                    <Input
                                        label="Search"
                                    />
                                </th> */}
                                        </tr>
                                        <tr className="py-3 uppercase">
                                            <th className="py-3">#</th>
                                            <th className="py-3">Location</th>
                                            <th className="py-3">Status</th>
                                            <th className="py-3">Modified</th>
                                            <th className="py-3">Created</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {meetingRooms.map((meetingRoom, index) => (
                                            <tr key={index} draggable className="border-t hover:bg-gray-50 button rounded-full">
                                                <td className="font-thin py-3 text-sm">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: meetingRoom.idColor }}></div>
                                                        {meetingRoom.number} ({meetingRoom.code})

                                                    </div>
                                                </td>
                                                <td className="font-thin py-3 text-sm">
                                                    <div className="">
                                                        {meetingRoom.floor.number} ({meetingRoom.floor.floorCode}) - {meetingRoom.floor.building.name}
                                                    </div>
                                                    <div className="">
                                                        {meetingRoom.floor.building.branch.name}
                                                    </div>
                                                </td>
                                                <td className="font-thin py-3 text-sm">
                                                    {meetingRoom.status}
                                                </td>
                                                <td className="font-thin py-3 text-sm">
                                                    {moment(meetingRoom.lastUpdated).format('lll')}
                                                </td>
                                                <td className="font-thin py-3 text-sm">
                                                    {moment(meetingRoom.createdDate).format('lll')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>

                                </table>
                            </div>)
                }
            </div>
        </div>
    );
};

export default ReceptionistMeetingRoom;
